// WidgetRenderer — the body of an AI-CREATED home widget (tickets/opus.md
// "Per-workspace AI-CREATED widgets"), the second half of Nika's mandate
// "new apps should be able to have their own widgets from appstore or created
// by user ai".
//
// It hosts inside the EXACT same chrome an app-store widget gets — the
// `HomeModuleWidget` Card/Header/IconTile/Body/IconMode shell, the 2×2 icon
// floor + maxW/maxH ceiling, `homeWidgetHue`, the gs-clamps, the Customize
// drawer, the context menu (pages/main.js). Nothing visual is designed twice;
// this component draws ONLY what goes inside the Body, exactly like a module's
// own `Bi*WidgetBody`.
//
// ─── Why this component exists at all ────────────────────────────────────────
// A module widget's `body` is a component KEY that resolves inside a real,
// reviewed, published module. An AI-created widget has no module behind it, so
// there is nothing to point at — and the tempting shortcut (store an
// `(el, s) => …` factory as a string and eval/new Function it here) would be a
// client-side RCE surface in every OTHER member's session. So the body is
// DATA: a fixed vocabulary of block types whose fields are PATHS into that
// widget's own resolved dataRecipe result, drawn by the reviewed interpreters
// in AiWidgetBlock.js. Nothing here evaluates a stored string, ever.
//
// ─── The render-time capability check ────────────────────────────────────────
// The server stamps `viewerCapability` on every listed row, resolved for the
// CALLING user (never the creator). Two consequences, both here:
//   * a denied viewer renders the inert notice below and NO fetch is made;
//   * the fetch itself still goes through `el.sdk.execute(...)` (functions/
//     aiWidgets.js `loadAiWidgetData`), so the real entity route authorizes the
//     viewer independently — a permission failure degrades to the same notice.
// A shared widget can therefore never render the creator's data to someone who
// could not have asked for it themselves.
export const WidgetRenderer = {
  flow: 'y',
  gap: 'Z',
  minHeight: '0',
  minWidth: '0',
  padding: 'Z B B B',

  // Kick the per-viewer resolve. Imperative-fetch-in-a-component is banned
  // (Rule 47); the SDK call lives in `functions/aiWidgets.js` and is invoked by
  // name, and it is idempotent per (widget, workspace, updatedAt) so these
  // re-fires (hosts re-render on every root update) do not loop.
  onRender: (el, s) => el.call('loadAiWidgetData', s.root, el.sdk, s),
  onUpdate: (el, s) => el.call('loadAiWidgetData', s.root, el.sdk, s),

  // Degraded, never broken and never silently over-privileged.
  Denied: {
    if: (el, s) =>
      !el.call('aiWidgetCapability', s, el.call('aiWidgetState', s.root, s.id)).allowed,
    flow: 'y',
    gap: 'Y',
    align: 'flex-start center',
    flex: '1',
    minHeight: '0',
    padding: 'Z',

    Icon: {
      icon: 'lockOutline',
      boxSize: 'B',
      color: 'caption'
    },
    Note: {
      tag: 'span',
      fontSize: 'Z2',
      lineHeight: '1.35',
      color: 'caption',
      textAlign: 'center',
      text: (el, s) =>
        el.call('aiWidgetCapability', s, el.call('aiWidgetState', s.root, s.id)).notice
    }
  },

  Blocks: {
    if: (el, s) => el.call('aiWidgetCapability', s, el.call('aiWidgetState', s.root, s.id)).allowed,
    flow: 'y',
    gap: 'Z',
    minHeight: '0',
    minWidth: '0',

    // One child per declared block, keyed by position+type so an edited body
    // (a block whose type changed at the same index) re-creates rather than
    // re-skinning the wrong interpreter. `widgetId` rides along because the
    // resolved data lives on root (the hosts' own state is reassigned from the
    // widget-set factory on every recompute — see functions/aiWidgets.js).
    children: (el, s) => {
      const blocks = s.body && Array.isArray(s.body.blocks) ? s.body.blocks : []
      return blocks.map((block, i) => ({
        ...block,
        key: `${i}-${block && block.type}`,
        widgetId: s.id
      }))
    },
    childExtends: 'AiWidgetBlock',
    childrenAs: 'state'
  }
}
